import { useMemo } from "react"

import useRewardsUserSelectStore from "./useRewardsUserSelectStore"
import { useFamilyQuery } from "../../../networking/queries"
import {
  useCustomRewardBalancesQuery,
  useCustomRewardLogsStatsQuery,
  useCustomRewardsQuery,
} from "../../../networking/rewards/queries"
import { FamilyQuerySelect } from "../../../types"
// This hook is used to get data about rewards and balances
// Also will get calculated values for the child selected
// on the Purchases page

const useRewards = () => {
  const { data: logs, isLoading: isLoadingLogs } =
    useCustomRewardLogsStatsQuery()
  const { data: balances, isLoading: isLoadingBalances } =
    useCustomRewardBalancesQuery()
  const { selectedUserId } = useRewardsUserSelectStore()
  const { data: children } = useFamilyQuery(FamilyQuerySelect.CHILDREN)
  const { data: rewards, isLoading: isLoadingRewards } = useCustomRewardsQuery()

  const hasPreviouslyPurchasedOrRedeemedRewards =
    (logs?.redeemed_all_time ?? 0) + (logs?.purchased_all_time ?? 0) > 0

  const selectedChild = children?.find(
    (child) => child.user.id === selectedUserId
  )

  const childRewardBalances =
    useMemo(
      () => balances?.filter((balance) => balance.user_id === selectedUserId),
      [balances, selectedUserId]
    ) || []

  const childHasAvailableRewardsToPurchase = useMemo(
    () =>
      selectedUserId !== null &&
      rewards?.some((reward) =>
        reward.assignments?.some(
          (assignment) => assignment.user_id === selectedUserId
        )
      ),
    [rewards, selectedUserId]
  )

  const childHasScreenTimeControls = useMemo(
    () =>
      selectedUserId !== null &&
      rewards?.some((reward) =>
        reward.assignments?.some(
          (assignment) =>
            assignment.user_id === selectedUserId &&
            assignment.screen_time_configurations.length > 0
        )
      ),
    [rewards, selectedUserId]
  )

  return {
    hasPreviouslyPurchasedOrRedeemedRewards,
    selectedChild,
    childRewardBalances,
    isLoadingBalances,
    isLoadingLogs,
    isLoadingRewards,
    childHasAvailableRewardsToPurchase,
    childHasScreenTimeControls,
    isLoading: isLoadingBalances || isLoadingLogs || isLoadingRewards,
  }
}

export default useRewards
